import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Spin, Alert, Button, Typography, Space, Result } from 'antd';
import { useAuth } from '../../hooks/useAuth';
import {
  getInvitationInfo,
  acceptInvitation,
} from '../../services/contractor-invitation.service';
import type { InvitationInfo } from '../../types/contractor-invitation.types';

const { Title, Paragraph } = Typography;

export const InviteAcceptPage: React.FC = () => {
  const { hash } = useParams<{ hash: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, refreshSession } = useAuth();

  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [contractorName, setContractorName] = useState('');

  useEffect(() => {
    if (!hash) return;
    getInvitationInfo(hash)
      .then((data) => {
        setInfo(data);
        setContractorName(data.contractorName);
      })
      .catch(() => setError('Convite inválido ou não encontrado.'))
      .finally(() => setLoading(false));
  }, [hash]);

  useEffect(() => {
    if (!hash || !isAuthenticated || !info || info.status !== 'PENDING' || done || error) return;

    if (user?.email && info.inviteeEmail && user.email.toLowerCase() !== info.inviteeEmail.toLowerCase()) {
      setError(
        `Este convite foi enviado para ${info.inviteeEmail}, mas você está logado como ${user.email}. Faça login com o email correto para aceitar o convite.`
      );
      return;
    }

    setAccepting(true);
    acceptInvitation(hash)
      .then(async () => {
        await refreshSession();
        setDone(true);
        setTimeout(() => navigate('/'), 1500);
      })
      .catch((err) => {
        const code = err?.response?.data?.error;
        if (code === 'INVITATION_EXPIRED') {
          setError('Este convite expirou. Solicite um novo convite ao administrador.');
        } else if (code === 'INVITATION_ALREADY_ACCEPTED') {
          setError('Este convite já foi aceito anteriormente.');
        } else if (code === 'USER_ALREADY_HAS_CONTRACTOR') {
          setError(
            'Seu usuário já está vinculado a outro contratante. Contate o administrador para remover o vínculo atual.'
          );
        } else {
          setError('Não foi possível aceitar o convite. Tente novamente.');
        }
      })
      .finally(() => setAccepting(false));
  }, [isAuthenticated, info, hash, done, error, navigate]);

  const redirectPath = encodeURIComponent(location.pathname);

  const handleLogin = () => {
    navigate(`/login?redirect=${redirectPath}`);
  };

  const handleRegister = () => {
    navigate(`/register?redirect=${redirectPath}`, {
      state: {
        prefillName: info?.inviteeName,
        prefillEmail: info?.inviteeEmail,
      },
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: 480, margin: '80px auto', padding: '0 16px' }}>
        <Alert type="error" message={error} showIcon />
      </div>
    );
  }

  if (info?.status === 'ACCEPTED') {
    return (
      <div style={{ maxWidth: 480, margin: '80px auto', padding: '0 16px' }}>
        <Alert type="info" message="Este convite já foi aceito." showIcon />
      </div>
    );
  }

  if (info?.status === 'EXPIRED') {
    return (
      <div style={{ maxWidth: 480, margin: '80px auto', padding: '0 16px' }}>
        <Alert
          type="warning"
          message="Convite expirado"
          description="Este convite não é mais válido. Solicite um novo convite ao administrador."
          showIcon
        />
      </div>
    );
  }

  if (done) {
    return (
      <Result
        status="success"
        title="Convite aceito!"
        subTitle={`Você agora tem acesso para gerenciar ${contractorName}. Redirecionando...`}
      />
    );
  }

  if (accepting) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" tip="Aceitando convite..." />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 480, margin: '80px auto', padding: '0 16px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
          <img src="/assets/brand/logo.png" alt="PePoll" style={{ height: 48, margin: '0 auto' }} />
          <Title level={3} style={{ marginBottom: 0 }}>Convite PePoll</Title>
          <Paragraph>
            Olá, <strong>{info?.inviteeName}</strong>!
          </Paragraph>
          <Paragraph>
            Você foi convidado para gerenciar a contratante{' '}
            <strong>{info?.contractorName}</strong> na plataforma PePoll.
          </Paragraph>
          {!isAuthenticated ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button type="primary" block onClick={handleLogin}>
                Fazer Login
              </Button>
              <Button block onClick={handleRegister}>
                Criar Conta
              </Button>
            </Space>
          ) : (
            <Paragraph type="secondary">Verificando seu acesso...</Paragraph>
          )}
        </Space>
      </Card>
    </div>
  );
};
